import EventBus, { Event, Unsubscriber } from '@/core/event-bus';
import PlayerStateRepository, { PlayerState } from '@/core/player-state';
import { isString } from 'lodash';
import { v4 as uuid } from 'uuid';
import playbackStuckTracker, { PlaybackInfo } from './playback-stuck-tracker';
import { sourceType } from '@/core/video-source';

import DeviceProvider, { ConnectionInformation } from './device.provider';

type AnalyticsSnapshotPlayer = {
    source?: Record<string, string|undefined>,
    buffer_count: number,
    buffer_duration: number,
    video_time: number
}

export type AnalyticsSnapshot = {
    player: AnalyticsSnapshotPlayer,
    network?: ConnectionInformation,
    name: string,
    playlist_id: string,
    playlist_uri?: string[] | string,
    playlist_bandwidth?: string,
    playlist_codecs?: string,
    playlist_resolution?: string,
    video_session_id: string
}

const LOG_NAME = 'SMVP analytics provider';
const INTERVAL_TIMER = 30 * 1000;
const NEW_SESSION_ID_THRESHOLD = 2 * 60 * 1000;

export default class AnalyticsProvider {
    private video_session_id: string|undefined = undefined;

    private playbackStuckTracker: Generator;

    private unsubscribers: Unsubscriber[] = [];

    private lastSnapshotTimestamp = -Infinity;

    private lastElapsedSecs = -Infinity;

    constructor(
        private eventBus: EventBus,
        private playerStateRepository: PlayerStateRepository,
        private intervalTimer: number = INTERVAL_TIMER,
        private newSessionIdThreshhold: number = NEW_SESSION_ID_THRESHOLD
    ) {
        console.debug(`[${LOG_NAME}] initializing...`);

        this.playbackStuckTracker = playbackStuckTracker(playerStateRepository);

        const { state, unsubscriber } = playerStateRepository
            .onStateChange(this.onStateChange.bind(this));

        this.unsubscribers.push(unsubscriber);

        if (state.isPlaying) {
            this.startSession();
        }

        console.debug(`[${LOG_NAME}] done initializing`);
    }

    public dispose(): void {
        this.endSession();

        this.playbackStuckTracker.next();
        this.playbackStuckTracker.return(undefined);

        for (const unsubscriber of this.unsubscribers) {
            if (typeof unsubscriber === 'function') {
                unsubscriber();
            }
        }
    }

    // private methods

    private getStats(overrides?: Partial<PlayerState>): Partial<AnalyticsSnapshot> {
        const stats: Partial<AnalyticsSnapshot> = {
            ...this.snapshot
        };

        if (overrides?.videoElapsedSecs && stats.player) {
            stats.player.video_time = overrides.videoElapsedSecs;
        }

        if (overrides?.videoSource && stats.player) {
            stats.player.source = {
                src: overrides.videoSource,
                type: sourceType(overrides.videoSource)
            };
        }

        return stats;
    }

    private onStateChange(state: Partial<PlayerState>, previousState: PlayerState, context: string): void {
        this.onSourceChange(state, previousState);

        if (state.isPlaying === true && previousState.isPlaying === false) {
            return this.startSession();
        } else if (state.isPlaying === false && previousState.isPlaying === true) {
            return this.endSession();
        }

        const elapsedSecs = state.videoElapsedSecs || 0;
        if (
            context === Event.seek &&
            elapsedSecs >= 0 &&
            !!this.videoSessionId
        ) {
            return this.resetSession(previousState);
        }

        this.doTrackingIteration();
    }

    private onSourceChange(state: Partial<PlayerState>, previousState: PlayerState) {
        if (!state.videoSource || !isString(state.videoSource)) {
            return;
        }

        const oldVal = (previousState.videoSource || '').split('?')[0];
        const newVal = state.videoSource.split('?')[0];

        if (oldVal !== newVal) {
            this.resetSession(previousState);
        }
    }

    private resetSession(previousState: Partial<PlayerState>): void {
        this.endSession(previousState);

        if (previousState.isPlaying) {
            this.startSession();
        }
    }

    private startSession(): void {
        this.video_session_id = uuid();

        const stats = this.trackAnalyticsSnapshot();

        this.lastSnapshotTimestamp = Date.now();
        this.lastElapsedSecs = this.videoElapsedSecs;

        console.debug(`[${LOG_NAME}] session started. ${this.videoSessionId}`, stats.player);
    }

    private endSession(overrides?: Partial<PlayerState>): void {
        if (!this.videoSessionId) {
            return;
        }

        const stats = this.trackAnalyticsSnapshot(overrides);

        console.debug(`[${LOG_NAME}] session ending. ${this.videoSessionId}`, stats.player);

        this.video_session_id = undefined;
    }

    private doTrackingIteration(): void {
        if (!this.videoSessionId) {
            return;
        }

        const interval = Date.now() - this.lastSnapshotTimestamp;

        /**
         * if there is too big a gap since the last snapshot, for
         * whatever reason, start a new session.
         */
        if (interval >= this.newSessionIdThreshhold) {
            this.endSession();
            this.startSession();
        } else if (
            interval >= this.intervalTimer &&
            this.lastElapsedSecs !== this.videoElapsedSecs
        ) {
            const stats = this.trackAnalyticsSnapshot();

            this.lastSnapshotTimestamp = Date.now();
            this.lastElapsedSecs = this.videoElapsedSecs;

            console.debug(`[${LOG_NAME}] interval snapshot emitted. ${this.videoSessionId}`, stats.player);
        }
    }

    private trackAnalyticsSnapshot(overrides?: Partial<PlayerState>): Partial<AnalyticsSnapshot> {
        const stats = this.getStats(overrides);

        this.emitToClient(stats);

        return stats;
    }

    private emitToClient(stats: Partial<AnalyticsSnapshot>): void {
        this.eventBus.emit(Event.analytics, { stats, getUpdatedStats: () => this.getStats() }, 'hls');
    }

    // getters and setters

    public get videoSessionId(): string|undefined {
        return this.video_session_id;
    }

    private get videoElapsedSecs(): number {
        return this.playerStateRepository.state?.videoElapsedSecs;
    }

    private get snapshot(): Partial<AnalyticsSnapshot> {
        const {
            stuckDiff,
            stuckDurationDiff
        } = this.playbackStuckTracker.next().value || {} as PlaybackInfo;

        const sourceUrl = this.playerStateRepository.state?.videoSource;

        return {
            name: 'video',
            player: {
                source: {
                    src: sourceUrl,
                    type: sourceType(sourceUrl),
                },
                buffer_count: stuckDiff,
                buffer_duration: stuckDurationDiff,
                video_time: this.videoElapsedSecs
            },
            network: DeviceProvider.connection,
            video_session_id: this.videoSessionId,
            ...this.levelStats
        };
    }

    private get levelStats(): Partial<AnalyticsSnapshot> {
        const currentLevel = this.playerStateRepository.state?.currentLevel;

        if (!currentLevel) return {};

        const playlist_uri = currentLevel.url;
        const playlist_id = isString(playlist_uri)
            ? playlist_uri.substring(playlist_uri.lastIndexOf('/') + 1)
            : undefined;

        return {
            playlist_uri,
            playlist_id,
            playlist_bandwidth: currentLevel.bandwidth,
            playlist_codecs: currentLevel.codecs,
            playlist_resolution: currentLevel.resolution
        };
    }
}
